import { Component, DestroyRef, Input, OnInit, computed, inject } from '@angular/core';
import { FormControl, NgControl, ReactiveFormsModule } from '@angular/forms';
import { IOptionItem, SelectComponent } from '@ui-lib/components';
import { debounceTime, startWith, Subject } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ClientDictionaryService } from '../client-selector/client-dictionary.service';
import { NoopValueAccessorDirective } from '../facility-select/noop-value-accessor.directive';

@Component({
  selector: 'mon-client-select',
  templateUrl: './client-select.component.html',
  styleUrl: './client-select.component.scss',
  imports: [ReactiveFormsModule, SelectComponent],
  hostDirectives: [NoopValueAccessorDirective],
  providers: [ClientDictionaryService],
})
export class ClientSelectComponent implements OnInit {
  private readonly ngControl = inject(NgControl, { self: true, optional: true });
  private readonly destroyRef = inject(DestroyRef);

  protected readonly clientDictionaryService = inject(ClientDictionaryService);

  private readonly keywordDebounceMs = 500 as const;
  private readonly keyword$ = new Subject<string>();

  @Input()
  public labelTranslateKey = 'monitoring.client';

  @Input()
  public searchTranslateKey = 'monitoring.search';

  @Input()
  public fullWidth = false;

  @Input()
  public isClearable = false;

  protected readonly clientOptions = computed<IOptionItem<string>[]>(() =>
    this.clientDictionaryService.items().map(
      (item): IOptionItem<string> => ({
        id: item.id,
        label: item.name,
      }),
    ),
  );

  public get control(): FormControl<IOptionItem<string> | null> {
    const control = this.ngControl?.control;

    if (!control) {
      throw new Error('mon-client-select must be used with formControl or formControlName');
    }

    if (!(control instanceof FormControl)) {
      throw new Error('mon-client-select supports only FormControl');
    }

    return control as FormControl<IOptionItem<string> | null>;
  }

  public ngOnInit(): void {
    this.keyword$
      .pipe(
        startWith(''),
        debounceTime(this.keywordDebounceMs),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((keyword): void => {
        this.clientDictionaryService.search(keyword.trim());
      });
  }

  public loadMore(): void {
    this.clientDictionaryService.loadMore();
  }

  public onKeywordChange(keyword: string): void {
    this.keyword$.next(keyword);
  }
}
